import SingleLineEllipsis from '@deltek/specification-client/core/Feedbacks/SingleLineEllipsis'
import { UserWithProjectRole } from '@deltek/specification-client/core/Models/firm'
import { isEqual } from '@deltek/specification-client/core/Utilities/objectUtils'
import {
    getNameInitials,
    suffixDimension,
} from '@deltek/specification-client/core/Utilities/stringUtils'
import SpecImage from '@deltek/specification-client/core/Widgets/SpecImage'
import Avatar from '@material-ui/core/Avatar'
import { makeStyles, Theme } from '@material-ui/core/styles'
import clsx from 'clsx'
import React from 'react'
import { CellProps } from 'react-table'

const useStyles = makeStyles((theme: Theme) => ({
    pictureDiv: {
        height: 24,
        width: 24,
        fontSize: theme.spacing(1),
        margin: 0,
        marginRight: theme.spacing(0.75),
        float: 'left',
    },
    changePicture: {
        height: 24,
        width: 24,
        backgroundColor: theme.palette.primary.main,
        fontSize: theme.spacing(1),
        margin: 0,
        marginRight: theme.spacing(0.75),
        float: 'left',
    },
    profileImageHeightWidth: {
        width: 24,
        height: 24,
    },
    profileImageImg: {
        objectFit: 'scale-down',
        objectPosition: 'center',
        borderRadius: '50%',
        float: 'left',
        marginRight: theme.spacing(0.75),
        border: '1px solid #FBB41F',
    },
    name: {
        color: theme.palette.secondary.main,
        cursor: 'pointer',
        paddingTop: 2,
    },
}))

interface ProjectTeamGridNameCellProps {
    selectedUserId: string | undefined | null
    nameCell: CellProps<UserWithProjectRole, string>
    onNameClick: (
        event: React.MouseEvent,
        userWithProjectRole: UserWithProjectRole
    ) => void
}
const ProjectTeamGridNameCell: React.FC<ProjectTeamGridNameCellProps> = React.memo(
    ({ selectedUserId, nameCell, onNameClick }) => {
        const { cell } = nameCell
        const classes = useStyles()
        const divRef = React.useRef<HTMLDivElement>(null)

        const handleNameClick = React.useCallback(
            (event: React.MouseEvent) => {
                onNameClick(event, cell.row.original)
            },
            [onNameClick, cell.row.original]
        )

        const { profileInfo } = cell.row.original.user
        const userInitials = getNameInitials(
            profileInfo?.firstName?.toUpperCase(),
            profileInfo?.lastName?.toUpperCase()
        )

        React.useEffect(() => {
            if (isEqual(selectedUserId, cell.row.original.user.id)) {
                divRef.current?.click()
            }
        }, [selectedUserId, cell.row.original])

        const nameDiv = React.useMemo(() => {
            return (
                <div
                    ref={divRef}
                    className={clsx('profileNameDiv', classes.name)}
                    onClick={handleNameClick}
                    data-testid="dvProjectTeamGridNameCell"
                >
                    <SingleLineEllipsis text={cell.value} />
                </div>
            )
        }, [cell.value, classes.name, handleNameClick])

        if (cell.isAggregated) return null

        return (
            <>
                {!!profileInfo?.imageUrl ? (
                    <div className={classes.pictureDiv}>
                        <SpecImage
                            alt="profile image"
                            src={suffixDimension({
                                filename: profileInfo?.imageUrl!,
                                dimension: { height: 150, width: 150 },
                            })}
                            additionalClassNames={clsx(
                                classes.profileImageImg,
                                classes.profileImageHeightWidth
                            )}
                            data-testid="profileImage"
                            height="24"
                            width="24"
                        />
                    </div>
                ) : (
                    <Avatar className={classes.changePicture}>
                        {userInitials}
                    </Avatar>
                )}
                {nameDiv}
            </>
        )
    }
)

export default ProjectTeamGridNameCell
